import React, { useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./styles";
import { DynamicContentType } from "type/dynamic-content";
import { Select } from "component/shared/select";
import { Switch } from "component/shared/switch";
import { isMarketMissing } from "shared/campaign-activation";
import { CampaignDisplayCategory } from "model/campaign-display-category";
import {
	categorySelectOptions,
	categoryValueToFk,
	categoryFkToValue,
} from "shared/campaign-grouping";
interface Market {
	id: number;
	name: string;
}

interface Props extends WithStyles<typeof styles> {
	name: string;
	description: string;
	dynamicContentType: DynamicContentType | undefined;
	emailBranding: boolean;
	marketId: number | undefined;
	markets: Market[];
	// MAX-3878: per-account display category. `categoryId` is the campaign FK (null = Uncategorized);
	// the select maps null ↔ the Uncategorized sentinel internally so callers stay in FK-space.
	categoryId: number | null | undefined;
	categories: CampaignDisplayCategory[];
	nameError?: boolean;
	// MAX-3879: initial active state, shown only at creation (the Add Campaign modal).
	// Omit on the edit/Settings form, where reactivation goes through the MAX-3860 confirmation flow instead.
	active?: boolean;
	// MAX-4201: bring Smart Content Source into view on open — set when arriving from the
	// Campaigns page's "Go to Settings" (`?focus=market`).
	scrollToMarket?: boolean;
	onScrolledToMarket?: () => void;
	onNameChange: (value: string) => void;
	onDescriptionChange: (value: string) => void;
	onDynamicContentTypeChange: (value: DynamicContentType | undefined) => void;
	onEmailBrandingChange: (value: boolean) => void;
	onMarketIdChange: (value: number | undefined) => void;
	onCategoryChange: (value: number | null) => void;
	onActiveChange?: (value: boolean) => void;
}

const Component: React.FC<Props> = ({
	classes = {},
	name,
	description,
	dynamicContentType,
	emailBranding,
	marketId,
	markets,
	categoryId,
	categories,
	nameError,
	active,
	scrollToMarket,
	onScrolledToMarket,
	onNameChange,
	onDescriptionChange,
	onDynamicContentTypeChange,
	onEmailBrandingChange,
	onMarketIdChange,
	onCategoryChange,
	onActiveChange,
}) => {
	const smartContentRef = useRef<HTMLDivElement>(null);

	// MAX-4201: a Market source with no Market can be saved but not activated. Derived from form
	// state, so choosing a Market clears the error and enables "Set as active" in place.
	const marketMissing = isMarketMissing(dynamicContentType, marketId);

	useEffect(() => {
		if (scrollToMarket && smartContentRef.current) {
			smartContentRef.current.scrollIntoView({ block: "center" });
			onScrolledToMarket?.();
		}
	}, [scrollToMarket, onScrolledToMarket]);

	const handleDynamicContentChange = (val: string) => {
		onDynamicContentTypeChange(val ? DynamicContentType.getById(val) : undefined);
	};

	const categoryOptions = categorySelectOptions(categories, { includeUncategorized: true });

	return (
		<>
			{/* General */}
			<div className={classes.section}>
				<Mui.Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
					General
				</Mui.Typography>
				<Mui.TextField
					error={nameError}
					helperText={nameError ? 'Name is required.' : undefined}
					variant="outlined"
					margin="dense"
					value={name}
					label="Campaign Name"
					onChange={(e) => onNameChange(e.target.value)}
					className={classes.fieldContainer}
					style={{ marginBottom: 24 }}
					fullWidth
				/>
				<Mui.TextField
					variant="outlined"
					margin="dense"
					value={description}
					label="Description"
					multiline
					minRows={4}
					onChange={(e) => onDescriptionChange(e.target.value)}
					className={`${classes.fieldContainer} ${classes.descriptionField}`}
					fullWidth
				/>
				{/* MAX-3878: Category select (mirrors the Market select). Defaults to General for new
					campaigns (set by the caller); maps the Uncategorized bucket to a null FK. */}
				<div className={classes.fieldContainer}>
					<Select
						variant="outlined"
						margin="dense"
						label="Category"
						fullWidth
						value={categoryFkToValue(categoryId)}
						options={categoryOptions}
						valueExtractor={(c) => c.id}
						labelExtractor={(c) => c.name}
						optionRenderer={(c) => <Mui.ListItemText primary={c.name} />}
						onChange={(value) => onCategoryChange(categoryValueToFk(value))}
					/>
				</div>
			</div>

			{/* Smart Content Source */}
			<div className={classes.section} ref={smartContentRef}>
				<Mui.Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
					Smart Content Source
				</Mui.Typography>
				<Mui.RadioGroup
					value={dynamicContentType?.id || ''}
					onChange={(e) => handleDynamicContentChange(e.target.value)}
				>
					{/* Lead Profile */}
					<Mui.FormControlLabel
						value={DynamicContentType.LEAD_PROFILE.id}
						control={<Mui.Radio color="secondary" size="small" />}
						style={{ alignItems: 'flex-start', marginBottom: 8 }}
						label={
							<div style={{ paddingTop: 2 }}>
								<Mui.Typography variant="body2" style={{ fontWeight: 600 }}>Lead Profile</Mui.Typography>
								<Mui.Typography variant="caption" color="textSecondary">
									Sends personalized listings and area statistics based on each lead's search behavior and listing views.
								</Mui.Typography>
							</div>
						}
					/>

					{/* Market — picker appears directly below when selected */}
					<Mui.FormControlLabel
						value={DynamicContentType.MARKET.id}
						control={<Mui.Radio color="secondary" size="small" />}
						style={{ alignItems: 'flex-start', marginBottom: dynamicContentType === DynamicContentType.MARKET ? 4 : 8 }}
						label={
							<div style={{ paddingTop: 2 }}>
								<Mui.Typography variant="body2" style={{ fontWeight: 600 }}>
									Market
									{/* MAX-4201: drawn here rather than via the Select's `required`, which CustomSelect
										repurposes to hide the blank row and never renders as an asterisk. */}
									{dynamicContentType === DynamicContentType.MARKET && (
										<span className={classes.requiredAsterisk} aria-hidden="true"> *</span>
									)}
								</Mui.Typography>
								<Mui.Typography variant="caption" color="textSecondary">
									Sends listings and area statistics for a specific Market you've configured in the Control Panel.
								</Mui.Typography>
								{dynamicContentType === DynamicContentType.MARKET && (
									<div className={classes.fieldContainer} style={{ marginTop: 8 }}>
										<Select
											variant="outlined"
											margin="dense"
											placeholder="Select a Market"
											fullWidth
											// MAX-4201: once a Market is set, drop the blank row so it can't be cleared —
											// a cleared Market was never persisted (the phantom clear).
											required={marketId != null}
											error={marketMissing}
											helperText={marketMissing
												? "A Market is required to activate this campaign, but you can still save changes without one."
												: undefined}
											value={marketId}
											options={markets}
											valueExtractor={(m) => m.id}
											labelExtractor={(m) => m.name}
											optionRenderer={(m) => <Mui.ListItemText primary={m.name} />}
											onChange={(value) => onMarketIdChange(value)}
										/>
									</div>
								)}
							</div>
						}
					/>
				</Mui.RadioGroup>
			</div>

			{/* MAX-3879: Status (create-only — reactivating an existing campaign goes through the MAX-3860 confirmation modal instead). */}
			{onActiveChange && (
				<div className={classes.section}>
					<Mui.Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
						Status
					</Mui.Typography>
					{marketMissing ? (
						// MAX-4201: disabled until a Market is chosen; the span lets the tooltip fire on a
						// disabled control (same pattern as the Campaigns page's Delete Category). It is focusable so
						// keyboard users can reach the explanation too, and a group so its label is read.
						<Mui.Tooltip title="Select a Market first. Market-based campaigns can't be activated without one.">
							<span tabIndex={0} role="group">
								<Switch
									checked={false}
									onChange={onActiveChange}
									label="Set as active"
									description="When enabled, leads can be enrolled in this campaign."
									disabled
								/>
							</span>
						</Mui.Tooltip>
					) : (
						<Switch
							checked={!!active}
							onChange={onActiveChange}
							label="Set as active"
							description="When enabled, leads can be enrolled in this campaign."
						/>
					)}
				</div>
			)}

			{/* Email Branding */}
			<div className={classes.section}>
				<Mui.Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
					Email Branding
				</Mui.Typography>
				<Switch
					checked={emailBranding}
					onChange={onEmailBrandingChange}
					label="Apply Email Branding"
					description={
						<>
							When enabled, your logo, photo, and business information will be included in campaign emails, helping reinforce your brand with every message sent to leads.
							<br />
							<Mui.Link
								href="https://kb.ihomefinder.com/s/article/email-branding"
								target="_blank"
								rel="noopener noreferrer"
								style={{ fontSize: 13, marginTop: 4, display: 'inline-block' }}
							>
								Learn More ↗
							</Mui.Link>
						</>
					}
				/>
			</div>
		</>
	);
};

export const CampaignForm = withStyles(Component, styles);
